/// <reference types="vite/client" />

import type { SgwtMiniFooterHTMLElement } from '@sgwt/sgwt-widgets-react';
import Collapse from 'bootstrap/js/dist/collapse';
import Dropdown from 'bootstrap/js/dist/dropdown';

import type { Feature } from './PublicPage';

type Paq = [string] | [string, string | number] | [string, string, string];

declare global {
  interface Window {
    _paq: Paq[];
  }
}

if (import.meta.env.DEV || import.meta.env.SSR) {
  await import('./public-page.react');
}

if (import.meta.env.DEV) {
  // Leave some time for React to initialize
  setTimeout(setupPageInteractivity, 250);
} else {
  setupPageInteractivity();
}

function setupPageInteractivity() {
  setupBootstrapComponents();

  setupMatomo();

  displayButtonsWhenConnected();

  setupRequestAccess();

  setupShare();

  setupNavBarActive();

  setUpNavbarSpies();

  setupWatchVideoButton();

  setupInsights();
}

function trackEvent(action: string) {
  window._paq.push(['trackEvent', 'Public page', action]);
}

function updateFeatureElement<T extends HTMLElement = HTMLElement>(feature: Feature, updater: (element: T) => void) {
  document.querySelectorAll<T>(`[data-feature='${feature}']`).forEach(updater);
}

function setupBootstrapComponents() {
  document.querySelectorAll('[data-bs-toggle=collapse]').forEach(btn => {
    btn.classList.add('collapse-icon-end');
    new Collapse(btn, { toggle: false });
  });
  document.querySelectorAll('.dropdown-toggle').forEach(dropdown => new Dropdown(dropdown));
}
type TopicsBySection = Record<string, Element[]>;
function setUpNavbarSpies() {
  const allTopics = Array.from(document.querySelectorAll("[id^='topic']"));
  const topicsBySection = allTopics.reduce((topicsBySection, e) => {
    const section = e.getAttribute('data-section')!;
    return { ...topicsBySection, [section]: [...(topicsBySection[section] ?? []), e] };
  }, {} as TopicsBySection);
  const observer = new IntersectionObserver(
    () => {
      const windowVerticalCenter = window.innerHeight / 2;
      const getVerticalCenterDifference = (e: Element) => {
        const { y, height } = e.getBoundingClientRect();
        return Math.abs(windowVerticalCenter - (y + height) / 2);
      };
      Object.entries(topicsBySection).forEach(([section, topics]) => {
        let nearestToVerticalCenterEntry: Element | undefined;
        if (topics.length > 1) {
          nearestToVerticalCenterEntry = topics.reduce((previous, current) =>
            getVerticalCenterDifference(previous) < getVerticalCenterDifference(current) ? previous : current,
          );
        }
        if (nearestToVerticalCenterEntry) {
          const allNavItemsForSectionSelector = `[id*='nav-to-topic'][data-section='${section}']`;
          document.querySelectorAll(allNavItemsForSectionSelector).forEach(e => e.classList.remove('active'));
          document
            .querySelectorAll(`[id$="nav-to-${nearestToVerticalCenterEntry.id}"]`)
            .forEach(e => e.classList.add('active'));
        }
      });
    },
    { threshold: 0.1 },
  );
  allTopics.forEach(t => observer.observe(t));
}

function setupWatchVideoButton() {
  document.getElementById('open-video-player-button')?.addEventListener('click', () => {
    const miniFooter = document.querySelector<SgwtMiniFooterHTMLElement>('sgwt-mini-footer');
    if (miniFooter && typeof miniFooter.getUserConsentOnQumu === 'function') {
      miniFooter.getUserConsentOnQumu().then(hasConsented => {
        const videoPlaceholder = document.getElementById('video-placeholder');
        videoPlaceholder?.classList.add('d-none');
        const playerCard = document.getElementById('video-player-card');

        const iframe = document.createElement('iframe');
        iframe.id = 'video-player';
        iframe.className = 'h-100 w-100';
        iframe.src = `https://socgen.qumucloud.com/view/64j0fj9kibyadquM2jjQx0?inline=true&autoPlay=true&embed=true&consented=${hasConsented}`;
        iframe.setAttribute('allowFullScreen', '');
        iframe.setAttribute('allow', 'fullscreen');
        playerCard?.appendChild(iframe);
      });
    }
  });

  updateFeatureElement('LifeCycle Video', el =>
    el.addEventListener('click', () => {
      window.open('https://socgen.qumucloud.com/view/nb11qHBw1oB', '_blank', 'popup,width=1200,height=675');
    }),
  );
}

function setupMatomo() {
  const { matomoBaseUrl, matomoSiteId } = window.sgmeConfiguration;
  window._paq = window._paq || [];
  window._paq.push(['requireCookieConsent']);
  window._paq.push(['trackPageView']);
  window._paq.push(['enableLinkTracking']);
  window._paq.push(['setTrackerUrl', matomoBaseUrl + '/piwik.php']);
  window._paq.push(['setSiteId', matomoSiteId]);
  const scriptTag = document.createElement('script');
  scriptTag.type = 'text/javascript';
  scriptTag.async = true;
  scriptTag.defer = true;
  scriptTag.src = matomoBaseUrl + '/piwik.js';
  document.head.appendChild(scriptTag);
  document.querySelectorAll<HTMLElement>('[data-feature]').forEach(item => {
    const feature = item.getAttribute('data-feature');
    if (feature) {
      item.addEventListener('click', () => {
        trackEvent(feature);
      });
    }
  });
  trackEvent('Visit');
}

function hasSgConnectKeyInLocalStorage() {
  let i = 0;
  let key: string | null;
  while ((key = localStorage.key(i++)) !== null) {
    if (key.startsWith('SGWTConnect')) {
      return true;
    }
  }
  return false;
}

function displayButtonsWhenConnected() {
  if (hasSgConnectKeyInLocalStorage()) {
    document.querySelectorAll('[data-toolbar]').forEach(toolbar => {
      if (toolbar.getAttribute('data-toolbar') === 'connected') {
        toolbar.classList.remove('d-none');
      } else {
        toolbar.classList.add('d-none');
      }
    });
  }
}

function setupRequestAccess() {
  const { requestAccessUrl } = window.sgmeConfiguration ?? {};
  const requestAccessLink = requestAccessUrl.endsWith('?returnurl=')
    ? `${requestAccessUrl}${window.location.href.replace(/\/public\/?/, '')}`
    : requestAccessUrl;
  updateFeatureElement<HTMLAnchorElement>('Request Access', link => {
    link.href = requestAccessLink;
  });
}

function setupShare() {
  const shareData = {
    text: 'SG Markets Structured Products',
    url: window.location.href,
  } satisfies ShareData;
  if (navigator.canShare && navigator.canShare(shareData)) {
    updateFeatureElement('Share', button => {
      button.onclick = () => {
        navigator.share(shareData);
        return false;
      };
    });
  }
}

function setupNavBarActive() {
  let previouslyActiveSection = 'about';
  document.addEventListener('scroll', function updateNavbarActiveSection() {
    const sectionIds: string[] = [];
    const sectionTops: number[] = [];

    document.querySelectorAll('section').forEach(section => {
      sectionIds.unshift(section.id);
      sectionTops.unshift(section.offsetTop - 1);
    });

    const viewTop = document.scrollingElement?.scrollTop ?? 0;
    const activeIndex = sectionTops.findIndex(sectionTop => sectionTop <= viewTop);
    const activeSection = activeIndex === -1 ? 'about' : sectionIds[activeIndex];
    if (previouslyActiveSection !== activeSection) {
      previouslyActiveSection = activeSection;
      document.querySelectorAll('[data-link]').forEach(link => {
        if (link.getAttribute('data-link') === activeSection) {
          link.classList.add('active');
          link.setAttribute('aria-current', 'page');
        } else {
          link.classList.remove('active');
          link.removeAttribute('aria-current');
        }
      });
    }
  });
}

type PublicationResult = { results: Publication[] };
type Publication = {
  tags: string;
  title: string;
  summary: string;
  publicationDateUtc: string;
  coverImageUrl: string;
  pageShortName: string;
};
const getLang = () => new URLSearchParams(window.location.search).get('lang') ?? 'EN';
async function setupInsights() {
  const { insightPublicApi, insightPublicPortalUrl } = window.sgmeConfiguration;
  const openInsightLink = document.getElementById('open-insight-research') as HTMLAnchorElement | null;
  if (openInsightLink) {
    openInsightLink.href = insightPublicPortalUrl;
  }
  const data = await fetch(
    `${insightPublicApi}api/v1/insight-publications?isCoverPublication=true&take=3&visibleOnWebsite=true`,
    { mode: 'cors' },
  );
  const json: PublicationResult = await data.json();
  const container = document.getElementById('insights')!;
  const template = document.getElementById('insight-template')!;
  const lang = getLang();
  const intl = new Intl.DateTimeFormat(lang?.toUpperCase(), { month: 'short', day: '2-digit', year: 'numeric' });
  json.results.forEach(({ title, summary, coverImageUrl, publicationDateUtc, tags, pageShortName }) => {
    const templateContent = template.cloneNode(true) as HTMLElement;
    templateContent.classList.remove('d-none');
    templateContent.id = title;
    templateContent.querySelector('.card-title')!.innerHTML = title;
    const a = templateContent.getElementsByTagName('a')[0];
    a.href = `${insightPublicPortalUrl}insights/${pageShortName}`;
    a.addEventListener('click', () => {
      trackEvent(`Open insight link ${pageShortName}`);
    });
    const p = templateContent.getElementsByTagName('p');
    p[0].innerHTML = intl.format(new Date(publicationDateUtc));
    p[1].innerHTML = summary;
    (templateContent.getElementsByTagName('img')[0] as HTMLImageElement).src = coverImageUrl;
    templateContent.querySelector('span:not(.card-title)')!.innerHTML = tags;
    container.append(templateContent);
  });
  template?.parentElement?.removeChild(template);
}
